import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { ACCOUNT_PREFIX } from 'restful/accounts/constants';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import { getLoyaltyBalance } from 'services/api/restful/generated/accounts/accounts';
import type {
  SaudiRewardsBalance,
  SaudiRewardsBalanceVerify,
} from 'services/api/restful/generated/nofomoRestApi.schemas';

type SaudiRewardsBalanceData = Pick<
  SaudiRewardsBalance,
  'balance' | 'memberId' | 'verificationToken' | 'verificationTokenExpiration'
>;

/** Cache key for balance written by this mutation — not a useQuery read. */
const accountSaudiRewardsBalanceQuery = queryOptions({
  queryKey: [ACCOUNT_PREFIX, 'saudi-rewards', 'balance'],
});

export const useSaudiRewardsBalance = () => {
  const queryClient = useQueryClient();

  const { isPending, isPaused, isError, mutate } = useMutation<
    SaudiRewardsBalanceData,
    RestApiError,
    SaudiRewardsBalanceVerify
  >({
    mutationFn: async (
      input: SaudiRewardsBalanceVerify,
    ): Promise<SaudiRewardsBalanceData> => {
      const { data } = await restCall(() => getLoyaltyBalance(input));
      return data;
    },
    onSuccess: (loyaltyBalance) => {
      queryClient.setQueryData(
        accountSaudiRewardsBalanceQuery.queryKey,
        loyaltyBalance,
        { updatedAt: 0 },
      );
    },
  });

  const balance = queryClient.getQueryData<SaudiRewardsBalanceData>(
    accountSaudiRewardsBalanceQuery.queryKey,
  );

  return {
    balance,
    requestBalance: mutate,
    isError,
    isLoading: isPending && !isPaused,
  };
};
